import React, { forwardRef, useRef, useImperativeHandle, useState } from "react";
import { RCCButton,RCCGrid, RCCIcon, RCCInput, RCCPopover, RCCRefModalProps, RCCTooltip } from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import FiltersContainer from "src/components/FiltersContainer";
import Modal from "src/components/Modal";
import subDays from "date-fns/subDays";
import Pagination from "src/components/Pagination";
import { useTranslation } from "src/context/Translations/context";
import formatDate from "src/utils/formatDate";
import LovBusinessGroups from "src/components/Lov/BusinessGroups";
import { useAuth } from "src/context/Auth/context";
import { IFiltersHistory, ISystemConnectionsHistoryRef } from "../interface";
import colUser from "./colUser";
import OverflowSpan, { StyledDivDescriptionAndAlertExclamation } from "../styles";

const ModalHistory: React.ForwardRefRenderFunction<ISystemConnectionsHistoryRef> = (_, ref) => {
	const { translate, currentLanguage } = useTranslation();
	const { superUser } = useAuth();
    
	const modalHistory = useRef<RCCRefModalProps>(null);
	const paginationRef = useRef<IForwardPagination>(null);
	
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IFiltersHistory>(
		{filter: "", group: "", endDate: new Date(), initDate: subDays(new Date(),30) }
	);	
	
	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters, 
		watch : watchFilters, 
		reset: resetFilters ,
		setValue: setValueFilters,
		control: controlFilters
	} = useForm();


	const submitFilters = (data: any) => {
		setFilters({ 
			filter: data.filter, 
			group: data.group?.value || data.group || "",
			endDate: data.endDate,
			initDate: data.initDate
		});
		paginationRef.current?.reload();
	};

	const openModal = async (initialFilters: Omit<IFiltersHistory, "endDate" | "initDate" | "filter">) => {
		setFilters({ 
			group: initialFilters?.group ?? "", 
			filter: "", 
			endDate: new Date(), 
			initDate: subDays(new Date(),30) 
		});
		modalHistory.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		openModal
	}));

	const colIp = (row: any) => (
		<RCCGrid
			container
			item
			alignItems="center"
			justifyContent="space-between"
		>
			<OverflowSpan title={row.efs002Remoteaddr}>
				{row.efs002Remoteaddr}
			</OverflowSpan>
			<StyledDivDescriptionAndAlertExclamation>
				<RCCPopover
					color="grey"
					header={{
					  title: translate("Descrição")
					}}
					body={{
					  text: row.efs002Useragent
					}}
					position="leftEnd"
				>
					<div className="infoCircleDescription-kr">
						<RCCIcon name="exclamationCircle" color="var(--grey)" size={16} />
					</div>
				</RCCPopover>
			</StyledDivDescriptionAndAlertExclamation>
		</RCCGrid>
	);

	return (
		<>
			<Modal 
				ref={modalHistory}
				header={{
					title: translate("Histórico"),
					icon: {
						name: "history"
					}
				}}
				footer= {{
					showSaveButton: false,
					cancelButtonText: translate("Cancelar")
				}}
				withoutForm
			>
				<RCCGrid
					container
					item 
					justifyContent="flex-end"
				>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || filters.filter || filters.group ? "blue" : "grey"}
						position="leftEnd"
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || filters.filter || filters.group ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</RCCGrid>
				
				{/* Filters */}
				{showFilters && (
					<FiltersContainer
						onSubmitFilters={handleSubmitFilters(submitFilters)}
						resetFilters={() => resetFilters(
							{ group: "", filter: "", endDate: null, initDate: null}
						)}
					>
						<RCCGrid
							container
							item
							spacing={2}
						>

							{superUser && (
								<RCCGrid
									container 
									item
									xs={12}
									sm={6}
									md={4}
									lg={4}
								>
									<LovBusinessGroups 
										control={controlFilters}
										name="group" 
										defaultValue={filters.group ?? ""}
										label={translate("Grupo de Empresas")}
										submit="value and label"
									/>
								</RCCGrid>
							)}

							
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								<DateTimePickerRange 
									type="datePicker"
									label={translate("Período")}
									labelSeparator={translate("até")}
									nameInit="initDate"
									nameEnd="endDate"
									setValue={setValueFilters}
									control={controlFilters}
									defaultValueEnd={filters.endDate}
									defaultValueInit={filters.initDate}	
								/> 
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								<RCCInput
									name="filter" 
									label={translate("Pesquisar")}
									register={registerFilters}
									defaultValue={watchFilters("filter")}
								/>
							</RCCGrid>
						</RCCGrid>
					</FiltersContainer>
				)}	

				<Pagination 
					ref={paginationRef}
					dataOrRoute="Connections/ListHistory"
					filtersData={{
						...filters,
						initDate: filters.initDate ? filters.initDate.toLocaleDateString() : "",
						endDate: filters.endDate ? filters.endDate.toLocaleDateString() : ""
					}}   
					columns={[
						{
							idColumn: "efs002Id",
							nameColumn: "#",
							justify: "center",
							xs: 12,
							sm: 1,
							md: 1,
							truncate: true
						},
						{
							idColumn: "efs002Usuario",
							nameColumn: translate("Usuário"),
							justify: "left",
							xs: 12,
							sm: 3,
							md: 3,
							truncate: true,
							cellRenderer: (row: any) => colUser(row)
						},
						{
							idColumn: "efs002Remoteaddr",
							nameColumn: translate("IP"),
							justify: "center",
							xs: 12,
							sm: 3,
							md: 3,
							truncate: true,
							cellRenderer: colIp
						},{
							idColumn: "efs002Dtlogon",
							nameColumn: `${translate("Data")} Logon`,
							justify: "left",
							xs: 12,
							sm: 2,
							md: 2,
							cellRenderer: (row: any) => formatDate(row.efs002Dtlogon,  currentLanguage, true),
							truncate: true
						},
						{
							idColumn: "efs002Dtlogoff",
							nameColumn: `${translate("Data")} Logoff` ,
							justify: "left",
							xs: 12,
							sm: 2,
							md: 2,
							cellRenderer: (row: any) => formatDate(row.efs002Dtlogoff,  currentLanguage, true),
							truncate: true
						},
						{
							idColumn: "",
							nameColumn: "",
							xs: 1
						}
					]}
				/>
			</Modal>
		</>
	);
};

export default forwardRef(ModalHistory);
